import React, { useState, useEffect } from 'react';
import { subscribeToCollection, saveDocument, deleteDocument } from '../firebase';
import { Itinerary, Leg } from '../models/FlightModels';
import RoleSignupList from './RoleSignupList';
import LoadingChip from './LoadingChip';
import Lightbox from './Lightbox';
import { getUserName } from '../utils/userUtils';
import { TRIP_DATES, dateToTripDay, parseTimeAndDate } from '../utils/dateUtils';
import { ensureAbsoluteUrl } from '../utils/urlUtils';
import { Plane } from 'lucide-react';

const normalizeDate = (dateStr) => {
  if (!dateStr) return "";
  const str = dateStr.trim().toLowerCase();

  // 1. Check YYYY-MM-DD
  const ymdMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymdMatch) {
    return `${parseInt(ymdMatch[2], 10)}/${parseInt(ymdMatch[3], 10)}`;
  }

  // 2. Check M/DD or MM/DD with optional weekday (e.g. "1/7", "Thursday 1/7")
  const mddMatch = str.match(/(\d{1,2})\/(\d{1,2})/);
  if (mddMatch) {
    return `${parseInt(mddMatch[1], 10)}/${parseInt(mddMatch[2], 10)}`;
  }

  // 3. Check "Month DD" (e.g. "Jun 13", "june 13")
  const months = {
    jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
    january: 1, february: 2, march: 3, april: 4, june: 6, july: 7, august: 8, september: 9, october: 10, november: 11, december: 12
  };

  for (const [mName, mNum] of Object.entries(months)) {
    if (str.includes(mName)) {
      const dayMatch = str.replace(mName, "").match(/\b(\d{1,2})\b/);
      if (dayMatch) {
        return `${mNum}/${parseInt(dayMatch[1], 10)}`;
      }
    }
  }

  return str;
};

const getLegSchedule = (leg, legIdx) => {
  if (leg.departure && (leg.departure.scheduled || leg.departure.estimated)) {
    const rawDepTime = leg.departure.scheduled || leg.departure.estimated || "TBD";
    const rawArrTime = leg.arrival && (leg.arrival.scheduled || leg.arrival.estimated) ? (leg.arrival.scheduled || leg.arrival.estimated) : "TBD";

    const depParsed = parseTimeAndDate(rawDepTime);
    const arrParsed = parseTimeAndDate(rawArrTime);

    let depTime = depParsed.time;
    let arrTime = arrParsed.time;

    const depDate = depParsed.date || leg.date || "";
    const arrDate = arrParsed.date || depDate;

    const normDepDate = normalizeDate(depDate);
    const normArrDate = normalizeDate(arrDate);

    // If arrival date is different than departure date, show date suffix on arrival time
    if (normDepDate && normArrDate && normDepDate !== normArrDate) {
      let dateDisplay = arrParsed.date;
      if (!dateDisplay || /^\d{4}-\d{2}-\d{2}$/.test(dateDisplay)) {
        dateDisplay = normArrDate;
      }
      arrTime = `${arrTime} (${dateDisplay})`;
    }

    const from = leg.departure.iata || leg.departure.airport || "TBD";
    const to = leg.arrival && (leg.arrival.iata || leg.arrival.airport) ? (leg.arrival.iata || leg.arrival.airport) : "TBD";
    const airline = leg.airline && leg.airline.airline_name ? leg.airline.airline_name : "TBD";
    const terminal = leg.departure.terminal || "";
    const gate = leg.departure.gate || "";
    const baggage = leg.arrival && leg.arrival.baggage ? leg.arrival.baggage : "";
    return { from, to, depTime, arrTime, airline, terminal, gate, baggage };
  }

  return {
    from: "TBD",
    to: "TBD",
    depTime: "TBD",
    arrTime: "TBD",
    airline: "TBD",
    terminal: "",
    gate: "",
    baggage: ""
  };
};

export default function FlightTrackerV3({ users = [] }) {
  const getTravelCompanionsForLeg = (leg, currentItineraryId) => {
    const companions = [];
    const carrier = (leg.airlineCode || '').trim().toUpperCase();
    const num = (leg.flightNumber || '').trim().toUpperCase();
    const dateStr = (leg.date || '').trim();
    const depap = (leg.departureAirport || '').trim().toUpperCase();

    if (!carrier || !num) return companions;

    flights.forEach(f => {
      if (f.id === currentItineraryId) return;

      const hasMatchingLeg = f.legs.some(l => {
        return (l.airlineCode || '').trim().toUpperCase() === carrier &&
          (l.flightNumber || '').trim().toUpperCase() === num &&
          (l.date || '').trim() === dateStr &&
          (l.departureAirport || '').trim().toUpperCase() === depap;
      });

      if (hasMatchingLeg) {
        f.passengers.forEach(p => {
          const name = getUserName(users, p);
          if (name && !companions.includes(name)) {
            companions.push(name);
          }
        });
      }
    });

    return companions;
  };

  const [itineraries, setItineraries] = useState([]);
  const [legs, setLegs] = useState([]);
  const [flights, setFlights] = useState([]); // Reconstructed linked models
  const [isLoading, setIsLoading] = useState(true);
  const [itinerariesLoading, setItinerariesLoading] = useState(true);
  const [legsLoading, setLegsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("All"); // All, Departure, Return
  const [showAddModal, setShowAddModal] = useState(false);

  // Unified Add / Edit Form Mode State
  const [formMode, setFormMode] = useState("add"); // "add" or "edit"
  const [editingItineraryId, setEditingItineraryId] = useState(null);

  // New Flight Form state
  const [newPassengers, setNewPassengers] = useState([]);
  const [isSigningUpPassenger, setIsSigningUpPassenger] = useState(false);
  const [passengerSelectValue, setPassengerSelectValue] = useState(users[0]?.id || "");
  const [newDirection, setNewDirection] = useState("Vacation Bound");
  const [formLegs, setFormLegs] = useState([{ id: 'temp_0', airlineCode: 'UA', flightNumber: '', date: '2027-01-07', departureAirport: '', flightyUrl: '' }]);



  // Subscribe to isolated itineraries
  useEffect(() => {
    const unsubscribe = subscribeToCollection("itineraries_v3", (data) => {
      setItineraries(data);
      setItinerariesLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Subscribe to isolated legs
  useEffect(() => {
    const unsubscribe = subscribeToCollection("legs_v3", (data) => {
      setLegs(data);
      setLegsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Real-time reconstruction of Flights array using Itinerary & Leg models
  useEffect(() => {
    const instantiatedLegs = legs.map(l => new Leg(l));
    const linkedItineraries = itineraries.map(it => {
      const itLegs = instantiatedLegs.filter(l => l.itineraryId === it.id);
      return new Itinerary({
        id: it.id,
        passengers: it.passengers,
        direction: it.direction,
        legs: itLegs
      });
    });

    setFlights(linkedItineraries);
  }, [itineraries, legs]);

  useEffect(() => {
    setIsLoading(itinerariesLoading || legsLoading);
  }, [itinerariesLoading, legsLoading]);

  // Deep-link handler: scroll to and highlight a specific itinerary via sessionStorage
  useEffect(() => {
    if (flights.length === 0) return;
    const targetId = sessionStorage.getItem('dayones_focus_itinerary');
    if (!targetId) return;
    sessionStorage.removeItem('dayones_focus_itinerary');
    setTimeout(() => {
      const el = document.getElementById(`itinerary-${targetId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.transition = 'box-shadow 0.3s ease, outline 0.3s ease';
        el.style.outline = '2px solid var(--color-primary)';
        el.style.boxShadow = '0 0 0 4px rgba(var(--color-primary-rgb), 0.2)';
        setTimeout(() => {
          el.style.outline = '';
          el.style.boxShadow = '';
        }, 2200);
      }
    }, 80);
  }, [flights]);


  // Form submission creating/editing isolated parent Itinerary and children Legs (with Smart Query API Diffing!)
  const handleSubmitItinerary = async (e) => {
    e.preventDefault();
    if (newPassengers.length === 0) return alert("Please add at least one passenger.");

    const invalidLeg = formLegs.some(l => !l.airlineCode.trim() || !l.flightNumber.trim() || !l.departureAirport.trim());
    if (invalidLeg) return alert("Please fill in airline codes, flight numbers and departing airports for all legs.");

    const directionValue = newDirection === "Vacation Bound" ? "incoming" : "outgoing";
    let itineraryId = editingItineraryId;

    if (formMode === "add") {
      const newItinerary = new Itinerary({
        passengers: newPassengers,
        direction: directionValue
      });
      itineraryId = newItinerary.id;
      await saveDocument("itineraries_v3", itineraryId, newItinerary.toFirestore());
    } else {
      // Edit Mode
      const updatedItinerary = {
        id: itineraryId,
        passengers: newPassengers,
        direction: directionValue
      };
      await saveDocument("itineraries_v3", itineraryId, updatedItinerary);

      // Leg Deletion Support: Remove any database legs that were deleted in the editor list
      const previousLegs = legs.filter(l => l.itineraryId === itineraryId);
      const currentLegIds = formLegs.map(l => l.id);
      const deletedLegs = previousLegs.filter(l => !currentLegIds.includes(l.id));

      if (deletedLegs.length > 0) {
        console.log("Deleting removed legs:", deletedLegs.map(l => l.id));
        const deletePromises = deletedLegs.map(l => deleteDocument("legs_v3", l.id));
        await Promise.all(deletePromises);
      }
    }

    // Write Legs to isolated collection (eager sync on query parameters diff or newly added leg!)
    const legPromises = formLegs.map(async (formLeg) => {
      const isNewLeg = String(formLeg.id).startsWith('temp_');
      let updatedLeg;
      let shouldSyncApi = false;

      if (isNewLeg) {
        updatedLeg = new Leg({
          itineraryId: itineraryId,
          airlineCode: formLeg.airlineCode.trim().toUpperCase(),
          flightNumber: formLeg.flightNumber.trim().toUpperCase(),
          date: formLeg.date || '2027-01-07',
          departureAirport: formLeg.departureAirport.trim().toUpperCase(),
          flightyUrl: ensureAbsoluteUrl(formLeg.flightyUrl)
        });
        /* shouldSyncApi = true */;
      } else {
        const existingLegData = legs.find(l => l.id === formLeg.id);
        if (existingLegData) {
          updatedLeg = new Leg(existingLegData);
          updatedLeg.flightyUrl = ensureAbsoluteUrl(formLeg.flightyUrl);
          updatedLeg.itineraryId = itineraryId;

          // Check if query parameters have changed from their original database values
          const orig = formLeg.originalData || {};
          const qParamsChanged =
            formLeg.airlineCode.trim().toUpperCase() !== (orig.airlineCode || '').trim().toUpperCase() ||
            formLeg.flightNumber.trim().toUpperCase() !== (orig.flightNumber || '').trim().toUpperCase() ||
            (formLeg.date || '2027-01-07') !== (orig.date || '2027-01-07') ||
            formLeg.departureAirport.trim().toUpperCase() !== (orig.departureAirport || '').trim().toUpperCase();

          if (qParamsChanged) {
            updatedLeg.airlineCode = formLeg.airlineCode.trim().toUpperCase();
            updatedLeg.flightNumber = formLeg.flightNumber.trim().toUpperCase();
            updatedLeg.date = formLeg.date || '2027-01-07';
            updatedLeg.departureAirport = formLeg.departureAirport.trim().toUpperCase();
            /* shouldSyncApi = true */;
          }
        } else {
          updatedLeg = new Leg({
            itineraryId: itineraryId,
            airlineCode: formLeg.airlineCode.trim().toUpperCase(),
            flightNumber: formLeg.flightNumber.trim().toUpperCase(),
            date: formLeg.date || '2027-01-07',
            departureAirport: formLeg.departureAirport.trim().toUpperCase(),
            flightyUrl: ensureAbsoluteUrl(formLeg.flightyUrl)
          });
          /* shouldSyncApi = true */;
        }
      }

      if (shouldSyncApi) {
        const nameVal = updatedLeg.airlineCode;
        const numVal = updatedLeg.flightNumber;
        const dateVal = updatedLeg.date.replace(/-/g, ''); // YYYYMMDD
        const depapVal = updatedLeg.departureAirport;

        const apiKey = "6a14dfc4d65e6162a62e7ca5";

        try {
          const url = `https://api.flightapi.io/airline/${apiKey}?num=${numVal}&name=${nameVal}&date=${dateVal}&depap=${depapVal}`;
          console.log("Unified Flight API sync query:", url);
          const res = await fetch(url);
          if (res.ok) {
            const json = await res.json();
            if (Array.isArray(json) && json.length >= 2) {
              updatedLeg.updateFromFlightApi(json);
            } else {
              console.warn(`Flight API returned invalid format for ${updatedLeg.airlineCode} ${updatedLeg.flightNumber}:`, json);
              updatedLeg.lastRefreshed = Date.now();
            }
          } else {
            updatedLeg.lastRefreshed = Date.now();
          }
        } catch (err) {
          console.warn(`Flight API call failed for ${updatedLeg.airlineCode} ${updatedLeg.flightNumber}:`, err.message);
          updatedLeg.lastRefreshed = Date.now();
        }
      }

      return saveDocument("legs_v3", updatedLeg.id, updatedLeg.toFirestore());
    });

    await Promise.all(legPromises);
    setShowAddModal(false);

    // Reset Form fields
    setNewPassengers([]);
    setEditingItineraryId(null);
    setFormLegs([{ id: 'temp_0', airlineCode: 'UA', flightNumber: '', date: '2027-01-07', departureAirport: '', flightyUrl: '' }]);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this flight itinerary? This will also remove all its associated flight legs.")) {
      await deleteDocument("itineraries_v3", id);
      const childLegs = legs.filter(l => l.itineraryId === id);
      const deletePromises = childLegs.map(l => deleteDocument("legs_v3", l.id));
      await Promise.all(deletePromises);
    }
  };

  const handleInitiateEditItinerary = (flight) => {
    setFormMode("edit");
    setEditingItineraryId(flight.id);
    setNewPassengers([...flight.passengers]);
    setNewDirection(flight.direction === "incoming" ? "Vacation Bound" : "Heading Home");

    const mappedLegs = flight.legs.map(leg => ({
      id: leg.id,
      airlineCode: leg.airlineCode || '',
      flightNumber: leg.flightNumber || '',
      date: leg.date || '',
      departureAirport: leg.departureAirport || '',
      flightyUrl: leg.flightyUrl || '',
      originalData: {
        airlineCode: leg.airlineCode || '',
        flightNumber: leg.flightNumber || '',
        date: leg.date || '',
        departureAirport: leg.departureAirport || ''
      }
    }));

    setFormLegs(mappedLegs);
    setShowAddModal(true);
  };



  const sortFlightsChronologically = (flightList) => {
    return [...flightList].sort((a, b) => {
      const dateA = a.getTravelDate() || '';
      const dateB = b.getTravelDate() || '';
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      // Fallback alphabetical sorting of passengers names
      const nameA = a.passengers.join(" & ");
      const nameB = b.passengers.join(" & ");
      return nameA.localeCompare(nameB);
    });
  };

  // Filter flights by tab
  const filteredFlights = flights.filter(f => {
    if (activeTab === "All") return true;
    const dirMap = {
      "Departure": "incoming",
      "Return": "outgoing"
    };
    return f.direction === dirMap[activeTab];
  });

  const getItineraryLastRefreshed = (flight) => {
    const times = flight.legs.map(l => l.lastRefreshed).filter(l => typeof l === 'number');
    if (times.length === 0) return "Never";
    const maxTime = Math.max(...times);
    const dateObj = new Date(maxTime);
    const now = new Date();
    if (dateObj.toDateString() === now.toDateString()) {
      return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    }
    return `${dateObj.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
  };

  return (
    <div className="tab-panel" style={{ fontFamily: 'var(--font-body) !important' }}>
      {/* Previously this rendered a local <style> block that redefined
         .boarding-pass, .airline-badge, .flight-status-badge, .status-*,
         .modal-overlay and .modal-content with old, non-theme-aware
         values (some referencing CSS variables that no longer exist, like
         --shadow-glass/--color-bg/--glow-accent). Because it was injected
         after the shared stylesheet, it was silently winning the cascade
         and fighting the unified card/modal styling in App.css/index.css.
         Removed — this component now just uses the shared classes, plus
         .flight-config-box/.btn-add-flight/.passenger-avatar/.passenger-badge/
         .btn-delete-pass/.btn-launch-direct, which weren't referenced
         anywhere in this file's JSX and were dead. */}

      {/* Page Title */}
      <div className="view-title-container">
        <div className="view-title">
          <h2>
            <Plane className="subpage-header-icon" strokeWidth={1.75} />
            Live Flights
          </h2>
        </div>
      </div>

      {/* Filter Tabs & Add Button Row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '28px', flexWrap: 'wrap' }}>
        <div className="schedule-day-tabs-wrapper" style={{ margin: 0 }}>
          {["All", "Departure", "Return"].map(t => (
            <button
              key={t}
              className={`schedule-day-tab ${activeTab === t ? 'active' : ''}`}
              onClick={() => setActiveTab(t)}
              style={{ padding: '8px 20px', fontSize: '0.8rem' }}
            >
              {t === "All" ? "🌍 All" : t === "Departure" ? "🛫 Incoming" : "🛬 Outgoing"}
            </button>
          ))}
        </div>
        <button
          className="btn-primary"
          onClick={() => {
            setFormMode("add");
            setEditingItineraryId(null);
            setNewPassengers([]);
            setNewDirection("Vacation Bound");
            setFormLegs([{ id: 'temp_0', airlineCode: '', flightNumber: '', date: '2027-01-07', departureAirport: '', flightyUrl: '' }]);
            setShowAddModal(true);
          }}
          style={{
            borderRadius: '0px',
            padding: '8px 20px',
            fontSize: '0.8rem',
            fontWeight: '700',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          + Add Flight
        </button>
      </div>

      {/* Flight Timeline Grid */}
      <div className="flight-timeline">
        {isLoading ? (
          <LoadingChip text="Syncing Flight Board 3.0..." />
        ) : filteredFlights.length === 0 ? (
          <div className="glass-panel" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            No flights found matching this category. Add a flight above!
          </div>
        ) : (
          (() => {
            const grouped = {};
            filteredFlights.forEach(flight => {
              const dateStr = flight.getTravelDate() || '2027-01-07';
              if (!grouped[dateStr]) grouped[dateStr] = [];
              grouped[dateStr].push(flight);
            });

            const sortedDates = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

            return sortedDates.map(dateStr => {
              const dayFlights = sortFlightsChronologically(grouped[dateStr]).sort((a, b) => getUserName(users, a.passengers[0]).localeCompare(getUserName(users, b.passengers[0])));

              return (
                <div key={dateStr} style={{ marginBottom: '40px' }}>
                  <h3 className="section-header-box" style={{
                    fontFamily: 'var(--font-title)',
                    fontSize: '1.25rem',
                    color: 'var(--color-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: '700'
                  }}>
                    {dateToTripDay(dateStr) || "Thursday 1/7"}
                  </h3>
                  <div className="flight-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '24px' }}>
                    {dayFlights.map(flight => {
                      const resolvedNames = flight.passengers.map(id => getUserName(users, id));
                      const passengerNameStr = resolvedNames.length <= 2
                        ? resolvedNames.join(" & ")
                        : resolvedNames.slice(0, -1).join(", ") + " & " + resolvedNames[resolvedNames.length - 1];

                      return (
                        <div key={flight.id} id={`itinerary-${flight.id}`} className="boarding-pass" style={{ height: 'auto', display: 'flex', flexDirection: 'column' }}>

                          {/* Boarding Pass Header */}
                          <div className="pass-header" style={{ padding: '14px 18px', borderBottom: '1px dashed var(--color-glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <span style={{ fontWeight: '800', fontSize: '1rem', letterSpacing: '0.03em', color: 'var(--color-text-main)' }}>
                                {passengerNameStr}
                              </span>
                            </div>
                            <span className="airline-badge" style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                              {flight.direction === "incoming" ? "Incoming" : "Outgoing"}
                            </span>
                          </div>

                          {/* Boarding Pass Body */}
                          <div className="pass-body" style={{ padding: '20px 18px', flex: 1, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            {/* Chronological Leg segments list */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', borderTop: '1px solid rgba(252, 249, 245,0.03)' }}>
                              <span style={{ fontSize: '0.65rem', textTransform: 'uppercase', color: 'var(--color-text-muted)', letterSpacing: '0.05em' }}>Flight Segments</span>

                              {flight.legs.map((leg, legIdx) => {
                                const schedule = getLegSchedule(leg, legIdx);
                                const legCompanions = getTravelCompanionsForLeg(leg, flight.id);
                                const legStatusText = (leg.flight_status && leg.flight_status !== 'scheduled')
                                  ? (leg.flight_status.charAt(0).toUpperCase() + leg.flight_status.slice(1))
                                  : "Scheduled";

                                let legStatusClass = "status-scheduled";
                                if (legStatusText.toLowerCase() === "landed") {
                                  legStatusClass = "status-landed";
                                } else if (legStatusText.toLowerCase() === "active" || legStatusText.toLowerCase() === "in-flight" || legStatusText.toLowerCase() === "in air" || legStatusText.toLowerCase() === "started") {
                                  legStatusClass = "status-inflight";
                                } else if (legStatusText.toLowerCase().includes("delay")) {
                                  legStatusClass = "status-delayed";
                                }

                                return (
                                  <div key={leg.id} style={{ background: 'var(--color-room-box-bg)', border: '1px solid var(--color-room-box-border)', borderRadius: '0px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <span style={{ fontWeight: '700', fontSize: '0.8rem', color: 'var(--color-text-main)' }}>
                                        {leg.airlineCode} {leg.flightNumber} ({schedule.from} ➔ {schedule.to})
                                      </span>
                                      <span className={`flight-status-badge ${legStatusClass}`} style={{ fontSize: '0.6rem', padding: '2px 6px' }}>
                                        {legStatusText}
                                      </span>
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
                                      <span>Dep: <strong style={{ color: 'var(--color-text-main)' }}>{schedule.depTime}</strong></span>
                                      <span>Arr: <strong style={{ color: 'var(--color-text-main)' }}>{schedule.arrTime}</strong></span>
                                    </div>

                                    {/* Terminal & Gate details */}
                                    {(schedule.terminal || schedule.gate || schedule.baggage) && (
                                      <div style={{ display: 'flex', gap: '8px', fontSize: '0.65rem', color: 'var(--color-text-muted)', borderTop: '1px solid rgba(252, 249, 245,0.02)', marginTop: '2px' }}>
                                        {schedule.terminal && <span>Term: <strong>{schedule.terminal}</strong></span>}
                                        {schedule.gate && <span>Gate: <strong>{schedule.gate}</strong></span>}
                                        {schedule.baggage && <span>Bag: <strong>{schedule.baggage}</strong></span>}
                                      </div>
                                    )}

                                    {/* Traveling with / Shared Connection Indicator */}
                                    {legCompanions.length > 0 && (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.65rem', color: 'var(--color-accent)', borderTop: '1px solid rgba(252, 249, 245,0.02)', paddingTop: '4px', marginTop: '2px', fontWeight: '600' }}>
                                        <span>Traveling with: <strong>{legCompanions.join(", ")}</strong></span>
                                      </div>
                                    )}

                                    {/* Segment-level Low-profile Flighty URL Integration */}
                                    {leg.flightyUrl && (
                                      <div style={{ borderTop: '1px dashed var(--color-glass-border)', paddingTop: '4px', marginTop: '0px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                          <a
                                            href={leg.flightyUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                              fontSize: '0.68rem',
                                              color: 'var(--color-primary)',
                                              fontWeight: '700',
                                              textDecoration: 'none',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '4px',
                                            }}
                                          >
                                            Track Live on Flighty ↗️
                                          </a>
                                        </div>
                                      </div>
                                    )}

                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Card Footer: Synced, Edit, and Remove Buttons */}
                          <div className="pass-footer" style={{ padding: '12px 18px', borderTop: '1px dashed var(--color-glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(252, 249, 245, 0.01)' }}>
                            <span style={{ fontSize: '0.68rem', color: 'var(--color-text-muted)', fontWeight: '600' }}>
                              Synced: <strong style={{ color: 'var(--color-text-main)' }}>{getItineraryLastRefreshed(flight)}</strong>
                            </span>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                              <button
                                type="button"
                                className="btn-inline-edit"
                                onClick={() => handleInitiateEditItinerary(flight)}
                                title="Edit Itinerary"
                              >
                                [Edit]
                              </button>
                              <button
                                className="btn-inline-delete-cross"
                                style={{ fontSize: '0.85rem', lineHeight: 1 }}
                                onClick={() => handleDelete(flight.id)}
                                title="Remove Itinerary"
                              >
                                ✕
                              </button>
                            </div>
                          </div>

                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()
        )}
      </div>

      {/* Add Connected Itinerary Modal Form */}
      <Lightbox
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={formMode === "edit" ? "Edit Itinerary" : "🛫 Add Flight"}
        maxWidth="520px"
      >
        <form onSubmit={handleSubmitItinerary}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '24px 28px' }}>

            {/* Passenger Selection */}
            <div className="form-group" style={{ margin: 0 }}>
              <RoleSignupList
                label="Passengers"
                names={newPassengers}
                isSigningUp={isSigningUpPassenger}
                userNameInput={passengerSelectValue}
                onUserNameChange={(val) => setPassengerSelectValue(val)}
                onStartSignup={() => {
                  setIsSigningUpPassenger(true);
                  setPassengerSelectValue(users[0]?.id || "");
                }}
                onConfirmSignup={() => {
                  if (passengerSelectValue && !newPassengers.includes(passengerSelectValue)) {
                    setNewPassengers([...newPassengers, passengerSelectValue]);
                  }
                  setIsSigningUpPassenger(false);
                }}
                onCancelSignup={() => setIsSigningUpPassenger(false)}
                onLeave={(nameToRemove) => {
                  setNewPassengers(newPassengers.filter(n => n !== nameToRemove));
                }}
                users={users}
                buttonLabel="+ Add Passenger"
                borderColor="var(--color-glass-border)"
                accentColor="var(--color-accent)"
              />
            </div>

            {/* Direction Choice */}
            <div className="form-group">
              <label>Direction</label>
              <select className="input-field" value={newDirection} onChange={e => setNewDirection(e.target.value)}>
                <option value="Vacation Bound">🛫 Vacation Bound (Incoming)</option>
                <option value="Heading Home">🛬 Heading Home (Outgoing)</option>
              </select>
            </div>

            {/* Connecting Leg List Builder */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 'bold', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Connected Flight Legs
              </label>

              {formLegs.map((leg, idx) => (
                <div key={leg.id} style={{ display: 'flex', flexDirection: 'column', gap: '8px', background: 'rgba(252, 249, 245,0.01)', padding: '12px', borderRadius: '0px', border: '1px solid var(--color-glass-border)' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 'bold', color: 'var(--color-accent)' }}>
                      Segment #{idx + 1}
                    </span>
                    <div style={{ flex: 1 }} />
                    {formLegs.length > 1 && (
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ padding: '2px 6px', fontSize: '0.65rem', color: 'var(--color-danger)', border: 'none', background: 'rgba(192, 57, 43, 0.08)', borderRadius: '0px', cursor: 'pointer' }}
                        onClick={() => setFormLegs(formLegs.filter(l => l.id !== leg.id))}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <input
                        type="text"
                        placeholder="Airline Code (e.g. UA)"
                        className="input-field"
                        value={leg.airlineCode}
                        onChange={e => {
                          const updated = [...formLegs];
                          updated[idx].airlineCode = e.target.value;
                          setFormLegs(updated);
                        }}
                        required
                        style={{ fontSize: '0.8rem', padding: '6px 10px' }}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <input
                        type="text"
                        placeholder="Flight Number (e.g. 123)"
                        className="input-field"
                        value={leg.flightNumber}
                        onChange={e => {
                          const updated = [...formLegs];
                          updated[idx].flightNumber = e.target.value;
                          setFormLegs(updated);
                        }}
                        required
                        style={{ fontSize: '0.8rem', padding: '6px 10px' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <select
                        className="input-field"
                        value={leg.date || '2027-01-07'}
                        onChange={e => {
                          const updated = [...formLegs];
                          updated[idx].date = e.target.value;
                          setFormLegs(updated);
                        }}
                        style={{ fontSize: '0.8rem', padding: '6px 10px' }}
                      >
                        {Object.entries(TRIP_DATES).map(([day, date]) => (
                          <option key={date} value={date}>{day}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <input
                        type="text"
                        placeholder="Departing airport code (e.g. BTV)"
                        className="input-field"
                        value={leg.departureAirport || ''}
                        onChange={e => {
                          const updated = [...formLegs];
                          updated[idx].departureAirport = e.target.value;
                          setFormLegs(updated);
                        }}
                        required
                        style={{ fontSize: '0.75rem', padding: '6px 10px' }}
                      />
                    </div>
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <input
                      type="text"
                      placeholder="Flighty Link (optional)"
                      className="input-field"
                      value={leg.flightyUrl || ''}
                      onChange={e => {
                        const updated = [...formLegs];
                        updated[idx].flightyUrl = e.target.value;
                        setFormLegs(updated);
                      }}
                      style={{ fontSize: '0.75rem', padding: '6px 10px' }}
                    />
                  </div>
                </div>
              ))}

              <button
                type="button"
                className="btn-secondary"
                style={{ padding: '8px', fontSize: '0.8rem', borderRadius: '0px', width: '100%', cursor: 'pointer', border: '1px dashed var(--color-glass-border)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', fontWeight: '700' }}
                onClick={() => setFormLegs([...formLegs, { id: `temp_${Date.now()}_${Math.random()}`, airlineCode: '', flightNumber: '', date: '2027-01-07', departureAirport: '', flightyUrl: '' }])}
              >
                + Add Connected Segment
              </button>
            </div>

            <button type="submit" className="btn-primary" style={{ marginTop: '16px', width: '100%', justifyContent: 'center', borderRadius: '0px', padding: '12px', fontWeight: '700', fontSize: '0.9rem' }}>
              {formMode === "edit" ? "Save Changes" : "Save Connected Itinerary"}
            </button>
          </div>
        </form>
      </Lightbox>
    </div>
  );
}
